import { useIntl } from "react-intl";
import { useState } from "react";
import { Combobox, Modal } from "medere-ui-framework";
import { exportBillHospitalizationPDF, exportPreinvoicedBillPDF, getBillHospitalizationDetailLevels, getBillHospitalizationViewModes } from "../../servicesHooks/BillHospitalization/useBillHospitalizationService";
import { ComboDataDTO, defaultComboDataDTO } from "../../dto/ComboDataDTO";
import { parseApiError } from "../../services/apiError";
import useNotify from "../../hooks/useNotify";

interface PrintBillHospitalizationModalProps {
    budget: number;
    /**
     * Factura ya prefacturada: el PDF muestra lo que se prefacturó (las órdenes), cada ítem por su total, sin dividir
     * entre paciente y obra social. Por eso no se pregunta el destinatario.
     */
    preinvoiced?: boolean;
    openPrintBudgetModal: boolean;
    setOpenPrintBudgetModal: (open: boolean) => void;
}

interface PrintBillHospitalizationModalValidationInterface {
    selectedViewMode: number | undefined;
    selectedDetailLevel: number | undefined;
}

const defaultValidation: PrintBillHospitalizationModalValidationInterface = {
    selectedViewMode: -1,
    selectedDetailLevel: -1
}

export default function PrintBillHospitalizationModal({ budget, preinvoiced = false, openPrintBudgetModal, setOpenPrintBudgetModal }: PrintBillHospitalizationModalProps) {

    const intl = useIntl();
    const notify = useNotify();
    const [isMobile] = useState(window.innerWidth <= 768);
    const [selectedViewMode, setSelectedViewMode] = useState<ComboDataDTO>(defaultComboDataDTO);
    const [selectedDetailLevel, setSelectedDetailLevel] = useState<ComboDataDTO>(defaultComboDataDTO);
    const [selectedItems, setSelectedItems] = useState<PrintBillHospitalizationModalValidationInterface>(defaultValidation);

    return (
        <Modal
            visible={openPrintBudgetModal}
            setVisible={setOpenPrintBudgetModal}
            className="custom-modal w-auto"
            header={<h6 className="modalTitle">{"Estás por descargar la facturación en PDF..."}</h6>}
            footerButtonRightText={intl.formatMessage({ id: "Download" })}
            footerButtonLeftText={intl.formatMessage({ id: "Cancel" })}
            onClickLeftBtn={() => setOpenPrintBudgetModal(false)}
            onClickRightBtn={() => {
                if (preinvoiced) {
                    exportPreinvoicedBillPDF(budget, selectedItems.selectedDetailLevel ?? 1).catch((error: unknown) => {
                        parseApiError(error); // loguea errorCode y path para cruzarlo con el backend
                        notify.error('No se pudo descargar el PDF. Intentá de nuevo.');
                    });
                    return;
                }
                exportBillHospitalizationPDF(budget, selectedItems.selectedViewMode ?? 1, selectedItems.selectedDetailLevel ?? 1);
            }}
            rightButtonDisabled={(!preinvoiced && selectedItems.selectedViewMode == -1) || selectedItems.selectedDetailLevel == -1}
            closable={false}
        >
            <div style={{ minWidth: isMobile ? "" : "150px" }}>
                <div className="flex flex-column gap-2">
                    <p className="ring-gray-400">
                        {preinvoiced
                            ? "Se muestran todos los ítems prefacturados, sin dividir entre paciente y obra social."
                            : "Configurá el contenido a mostrar en la facturación"}
                    </p>
                    <div className="flex flex-row gap-1">
                        {!preinvoiced && (
                            <Combobox
                                label={"Destinatario"}
                                className={`input-common`}
                                getItems={getBillHospitalizationViewModes}
                                optionLabel='name'
                                placeholder="Seleccionar"
                                value={selectedViewMode.name}
                                setValue={(e: ComboDataDTO) => {
                                    setSelectedViewMode(e);
                                    setSelectedItems({ ...selectedItems, selectedViewMode: e.id ?? 1 });
                                }}
                            />
                        )}
                        <Combobox
                            label={"Valores a mostrar"}
                            className={`input-common`}
                            getItems={getBillHospitalizationDetailLevels}
                            optionLabel='name'
                            placeholder="Seleccionar"
                            value={selectedDetailLevel.name}
                            setValue={(e: ComboDataDTO) => {
                                setSelectedDetailLevel(e);
                                setSelectedItems({ ...selectedItems, selectedDetailLevel: e.id ?? 1 });
                            }}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
}
